import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { AgGridModule } from 'ag-grid-angular';
import { BodyScrollEvent, ColDef, GridOptions, RowClickedEvent } from 'ag-grid-community';
import { ClaimsService } from '../../../core/services/claims.service';
import { ICalculatedBrandCompany } from '../../interfaces/claims.model';
import type { IBrandSearchDialogData, IBrandSearchDialogResult } from '../../interfaces/brand-search-dialog.interface';
import { OVERLAY_TEMPLATES } from '../../constants/brand-company.constants';

@Component({
  selector: 'emma-brand-search-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, AgGridModule],
  templateUrl: './brand-search-dialog.component.html',
  styleUrls: ['./brand-search-dialog.component.scss']
})
export class BrandSearchDialogComponent implements OnInit {
  public searchText = '';
  public searchResults: Array<ICalculatedBrandCompany> = [];
  public allSearchResults: Array<ICalculatedBrandCompany> = [];
  public gridData: Array<ICalculatedBrandCompany> = [];
  public selectedBrand: ICalculatedBrandCompany | null = null;
  public isLoading = false;
  public minSearchLength = 3;
  public debounceInMs = 300;
  private readonly maxResultsToDisplay: number = 25;
  private searchTimeout: ReturnType<typeof setTimeout> | null = null;
  public excessData = false;
  public count = 0;
  public showLoadAllMatches = false;
  public gridOptions!: GridOptions;
  public overlayNoRowsTemplate = OVERLAY_TEMPLATES.noRowsTemplateForBrand;
  public readonly agGridInputs = { tooltipShowDelayMilliSeconds: 0 };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IBrandSearchDialogData,
    private readonly dialogRef: MatDialogRef<BrandSearchDialogComponent>,
    private readonly claimsService: ClaimsService
  ) {}

  public ngOnInit(): void {
    const columnDefs: Array<ColDef> = [
      {
        headerName: 'Brand Name',
        field: 'brandCompanyName',
        flex: 1,
        sortable: true,
      },
    ];

    this.gridOptions = {
      rowData: this.gridData,
      columnDefs: columnDefs,
      rowHeight: 30,
      headerHeight: 0,
      suppressHorizontalScroll: false,
      onColumnResized: (event) => {
        if (event?.finished) {
          this.gridOptions.api?.refreshCells();
        }
      },
    } as GridOptions;
  }

  public onSearchInput(): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    const trimmedText = this.searchText.trim();

    if (trimmedText.length < this.minSearchLength) {
      this.searchResults = [];
      this.isLoading = false;
      return;
    }
    this.isLoading = true;

    this.searchTimeout = setTimeout(() => {
      this.performSearch(trimmedText);
    }, this.debounceInMs);
  }

  private performSearch(companySearchTerm: string): void {
     if (Array.isArray(this.data.brands) && this.data.brands.length > 0) {
       const filtered: Array<ICalculatedBrandCompany> = this.data.brands.filter((c: ICalculatedBrandCompany) =>
         c.brandCompanyName.toLowerCase().includes(companySearchTerm.toLowerCase())
       );
       this.allSearchResults = filtered
         .sort((a: ICalculatedBrandCompany, b: ICalculatedBrandCompany) =>
           a.brandCompanyName.localeCompare(b.brandCompanyName, undefined, { sensitivity: 'base' })
         );
       this.processSearchResults();
       this.isLoading = false;
       return;
     }
     if (!this.data.projectCode) {
       this.isLoading = false;
       return;
     }
  }

  private processSearchResults(): void {
    if (this.allSearchResults.length > this.maxResultsToDisplay) {
      this.gridData = this.allSearchResults.slice(0, this.maxResultsToDisplay);
      this.count = this.allSearchResults.length;
      this.excessData = true;
    } else {
      this.gridData = this.allSearchResults;
      this.excessData = false;
    }
    this.searchResults = this.gridData;
    this.processGridSearchData(this.gridData);
    this.showLoadAllMatches = false;
  }

  private processGridSearchData(searchResults: Array<ICalculatedBrandCompany>): void {
    this.gridData = searchResults;
    if (this.gridOptions?.rowData) {
      this.gridOptions.rowData = this.gridData;
    }
    this.gridOptions?.api?.setRowData(this.gridData);
  }

  public loadAllSearchResults(): void {
    this.gridData = this.allSearchResults;
    this.searchResults = this.allSearchResults;
    this.excessData = false;
    this.processGridSearchData(this.gridData);
  }

  public onBodyScrollEnd(event: BodyScrollEvent): void {
    if (event.direction !== 'vertical') {
      return;
    }
    const bodyViewport = document.querySelector(
      '.e-brand-search-grid div[ref="eBodyViewport"]'
    ) as HTMLElement;
    if (!bodyViewport || !this.excessData || this.showLoadAllMatches) {
      return;
    }
    this.showLoadAllMatches = this.isScrolledToBottom(bodyViewport);
  }

  private isScrolledToBottom(bodyViewport: HTMLElement): boolean {
    const scrollTop = bodyViewport.scrollTop;
    const scrollHeight = bodyViewport.scrollHeight;
    const clientHeight = bodyViewport.clientHeight;
    return scrollTop + clientHeight >= scrollHeight - 1;
  }

  public onSearchResultClicked(event: RowClickedEvent): void {
    const selectedBrandRow: ICalculatedBrandCompany = event?.data;
    if (selectedBrandRow) {
      this.onSelect(selectedBrandRow);
    }
  }

  public isSelected(brand: ICalculatedBrandCompany): boolean {
    return this.selectedBrand?.id === brand.id;
  }

  public onSelect(brand: ICalculatedBrandCompany): void {
    this.selectedBrand = brand;
  }

  public clearSelection(): void {
    this.selectedBrand = null;
  }

  public onAdd(): void {
    if (this.selectedBrand) {
      const result: IBrandSearchDialogResult = {
        brand: this.selectedBrand
      };
      this.dialogRef.close(result);
    }
  }

  public onCancel(): void {
    this.dialogRef.close();
  }

  public onClose(): void {
    this.dialogRef.close();
  }
}
